import React, { useState, useEffect, useMemo } from 'react';
import { tradingService } from '../../../services/tradingService';
import PlotlyChart from '../../../components/charts/PlotlyChart';

const COLORS = [
  '#c9a96e', '#6366f1', '#22c55e', '#ef4444', '#3b82f6',
  '#f59e0b', '#ec4899', '#14b8a6', '#8b5cf6', '#f97316',
];

const fmtPct = (v) => (v != null ? (v * 100).toFixed(1) + '%' : 'N/A');
const fmtDate = (v) => {
  if (!v) return 'N/A';
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

const DrawdownPage = ({ analyticsParams, paramsKey }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!analyticsParams) return;
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      try {
        const fetchFn = () => tradingService.analyticsDrawdown(analyticsParams);
        const res = await fetchFn();
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [paramsKey]);

  // Drawdown time series traces
  const ddTraces = useMemo(() => {
    if (!data?.drawdown) return [];
    const { index: dates, columns: names, data: rows } = data.drawdown;

    const traces = names.map((name, ci) => {
      const xArr = [], yArr = [];
      dates.forEach((d, i) => {
        if (rows[i][ci] != null) { xArr.push(d); yArr.push(rows[i][ci]); }
      });
      const isBenchmark = name === 'SPX Index' || name === 'SPY';
      return {
        x: xArr, y: yArr, name, type: 'scatter', mode: 'lines',
        fill: isBenchmark ? undefined : 'tozeroy',
        fillcolor: isBenchmark ? undefined : (COLORS[ci % COLORS.length] + '30'),
        line: {
          color: isBenchmark ? '#DC143C' : COLORS[ci % COLORS.length],
          width: isBenchmark ? 2 : 1.5,
          dash: isBenchmark ? 'dash' : 'solid',
        },
        hovertemplate: `%{x|%b %Y}<br>${name}: %{y:.2%}<extra></extra>`,
      };
    });

    return traces;
  }, [data]);

  // Max drawdown bar chart
  const maxDdTrace = useMemo(() => {
    if (!data?.max_drawdown) return null;
    const entries = Object.entries(data.max_drawdown)
      .filter(([, v]) => v != null)
      .sort(([, a], [, b]) => a - b);

    return {
      x: entries.map(([, v]) => v),
      y: entries.map(([name]) => name),
      type: 'bar', orientation: 'h',
      marker: {
        color: entries.map(([name], i) =>
          (name === 'SPX Index' || name === 'SPY') ? '#DC143C' : COLORS[i % COLORS.length]
        ),
      },
      text: entries.map(([, v]) => fmtPct(v)),
      textposition: 'auto',
      hovertemplate: '%{y}: %{x:.2%}<extra></extra>',
    };
  }, [data]);

  // Worst episodes
  const episodes = data?.episodes || {};

  if (loading) return <div className="text-center py-12 text-gray-500">Loading...</div>;
  if (!data) return <div className="text-center py-12 text-gray-500">No data -- run some backtests first</div>;

  const sortedFunds = maxDdTrace ? maxDdTrace.y : [];

  return (
    <div>
      {/* Drawdown Time Series */}
      {ddTraces.length > 0 && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Drawdown Over Time</h3>
          <PlotlyChart
            data={ddTraces}
            layout={{
              paper_bgcolor: 'transparent', plot_bgcolor: 'transparent',
              font: { color: '#9ca3af', size: 10 },
              yaxis: { title: 'Drawdown', tickformat: '.0%' },
              xaxis: { title: '' },
              legend: { orientation: 'h', yanchor: 'bottom', y: -0.25, xanchor: 'center', x: 0.5 },
            }}
            style={{ minHeight: 350 }}
          />
        </div>
      )}

      {/* Max Drawdown Bar Chart */}
      {maxDdTrace && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Maximum Drawdown Comparison</h3>
          <PlotlyChart
            data={[maxDdTrace]}
            layout={{
              paper_bgcolor: 'transparent', plot_bgcolor: 'transparent',
              font: { color: '#9ca3af', size: 10 },
              xaxis: { title: 'Max Drawdown', tickformat: '.0%' },
              margin: { l: 180, r: 15, t: 10, b: 40 },
              showlegend: false,
            }}
            style={{ minHeight: Math.max(250, sortedFunds.length * 40) }}
          />
        </div>
      )}

      {/* Worst Episodes Tables */}
      {Object.keys(episodes).length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Object.entries(episodes).map(([strategy, eps]) => {
            const top5 = (eps || []).slice(0, 5);
            if (!top5.length) return null;
            const isBenchmark = strategy === 'SPX Index' || strategy === 'SPY';
            return (
              <div key={strategy} className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
                <div
                  className="px-4 py-3 font-semibold text-sm border-b border-gray-200 dark:border-gray-800 bg-gray-100 dark:bg-gray-800/40"
                  style={{ borderLeft: `4px solid ${isBenchmark ? '#DC143C' : '#c9a96e'}` }}
                >
                  <span className={isBenchmark ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-200'}>
                    {strategy} -- Worst Drawdowns
                  </span>
                </div>
                <div className="overflow-x-auto scrollbar-thin">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-200/70 dark:bg-gray-800/50 text-xs uppercase tracking-wider text-gray-700 dark:text-gray-500">
                      <tr>
                        <th className="px-3 py-2.5 text-left">#</th>
                        <th className="px-3 py-2.5 text-left">Start</th>
                        <th className="px-3 py-2.5 text-left">Trough</th>
                        <th className="px-3 py-2.5 text-left">End</th>
                        <th className="px-3 py-2.5 text-right">Depth</th>
                        <th className="px-3 py-2.5 text-right">Duration</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {top5.map((ep, i) => (
                        <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                          <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{i + 1}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{fmtDate(ep.Start)}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{fmtDate(ep.Trough)}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{fmtDate(ep.End) || 'Ongoing'}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-semibold text-red-500">{fmtPct(ep.Depth)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{ep.Duration != null ? `${ep.Duration}m` : 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DrawdownPage;
